import React from 'react';
const { Icon, Logo, Button, StatusPill, Card, Avatar, BarMini, Donut, Tabs, IndiceBadge } = window;
/* ============================================================
   BOOKER — Tela: Visão Executiva
   Espelho da pasta de contratos (SharePoint): renovação anual
   automática, status Ativo/Encerrado, correção pelo índice consultiva.
   ============================================================ */
var { useState, useEffect, useRef, useMemo } = React;

function Dashboard({ onNavigate, sync }) {
  const DB = window.DB;
  const { fmtBRL, fmtData, fmtNum, diasAte, calc } = DB;
  // recalcula quando os dados reais terminam de carregar (sync muda no App)
  const todos = useMemo(() => DB.contratosComCliente(), [sync]);

  const [fStatus, setFStatus] = useState('all');
  const [fJanela, setFJanela] = useState('all');

  const dados = useMemo(() => todos.filter(k => {
    if (fStatus !== 'all' && k.status !== fStatus) return false;
    if (fJanela !== 'all') { const d = k.dias; if (!(d >= 0 && d <= +fJanela)) return false; }
    return true;
  }), [todos, fStatus, fJanela]);

  // Nova realidade: renovação anual automática. Tudo que não está Encerrado é
  // vigente; contratos nunca "vencem" — renovam no próximo aniversário.
  const ativos = dados.filter(k => k.status !== 'Encerrado');
  const encerrados = dados.filter(k => k.status === 'Encerrado');
  const encerradosN = encerrados.length;
  const aRenovar30 = ativos.filter(k => k.dias >= 0 && k.dias <= 30);
  const emDia = ativos.filter(k => !(k.dias >= 0 && k.dias <= 30)).length;
  const valorCarteira = ativos.reduce((s, k) => s + k.valorAtual, 0);

  // Correção pelo índice (consultiva): valor corrigido a partir do valor/data
  // inicial vs. valor cobrado. Só carteira ativa; só quem está ABAIXO do índice.
  const divergencias = ativos.map(k => ({ k, d: calc.correcaoAutomatica(k) })).filter(x => x.d.divergencia > 0.5);
  const totalDiverg = divergencias.reduce((s, x) => s + x.d.divergencia, 0);

  // status donut — 3 faixas mutuamente exclusivas que somam o total
  const statusCounts = ['Em dia', 'A renovar', 'Encerrado'].map(st => {
    let val;
    if (st === 'A renovar') val = aRenovar30.length;
    else if (st === 'Em dia') val = emDia;
    else val = encerradosN;
    return { label: st, value: val };
  });
  const stColors = { 'Em dia': 'var(--ok)', 'A renovar': 'var(--booker-orange)', Encerrado: 'var(--text-faint)' };

  // renovações próximos 6 meses (só carteira ativa)
  const meses = [];
  for (let i = 0; i < 6; i++) { const d = DB.addMeses(DB.HOJE, i); meses.push({ d, label: d.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', ''), count: 0, valor: 0 }); }
  ativos.forEach(k => {
    const venc = DB.parseData(k.dataVencimento);
    meses.forEach(m => { if (venc.getMonth() === m.d.getMonth() && venc.getFullYear() === m.d.getFullYear()) { m.count++; m.valor += k.valorAtual; } });
  });

  // índices distribuição
  const idxDist = Object.keys(DB.INDICES).map(key => ({ key, label: DB.INDICES[key].label, color: DB.INDICES[key].color,
    count: ativos.filter(k => k.indice === key).length })).filter(x => x.count > 0);

  // alertas — renovações próximas + valores a revisar (correção pelo índice
  // depende de um valor inicial confiável; zerado/baixo demais é dado a checar)
  const alertas = [];
  ativos.forEach(k => {
    const vi = k.valorInicial;
    if (vi == null || vi <= 0) alertas.push({ tipo: 'Valor', prio: 4, k, msg: 'Valor inicial não informado — correção pelo índice indisponível', icon: 'alert', color: 'var(--danger)' });
    else if (vi < 100 && !k.valorConferido) alertas.push({ tipo: 'Valor', prio: 4, k, msg: `Valor inicial muito baixo (${fmtBRL(vi)}) — verificar ou confirmar`, icon: 'alert', color: 'var(--danger)' });
    if (k.dias >= 0 && k.dias <= 30) alertas.push({ tipo: 'Renovação', prio: 3, k, msg: `Renova em ${k.dias} dias — janela de 30 dias`, icon: 'clock', color: 'var(--booker-orange)' });
  });
  alertas.sort((a, b) => b.prio - a.prio);

  const Sel = ({ value, onChange, children, w }) => (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <select value={value} onChange={e => onChange(e.target.value)} style={{
        appearance: 'none', padding: '9px 34px 9px 13px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border-strong)',
        background: 'var(--surface)', color: 'var(--text-strong)', fontWeight: 600, fontSize: 13.5, cursor: 'pointer', minWidth: w || 'auto' }}>
        {children}
      </select>
      <Icon name="chevronD" size={15} style={{ position: 'absolute', right: 11, top: 11, pointerEvents: 'none', color: 'var(--text-muted)' }} />
    </div>
  );

  return (
    <div style={{ animation: 'fadeUp .4s ease both' }}>
      {/* Header + filtros */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 22 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-.02em' }}>Visão Executiva</h1>
          <p style={{ margin: '5px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Carteira de contratos · {dados.length} no espelho da pasta · renovação anual automática</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Sel value={fStatus} onChange={setFStatus}>
            <option value="all">Todos os status</option>
            <option>Ativo</option><option>Encerrado</option>
          </Sel>
          <Sel value={fJanela} onChange={setFJanela}>
            <option value="all">Qualquer prazo</option>
            <option value="30">Renova em 30d</option><option value="60">Renova em 60d</option><option value="90">Renova em 90d</option>
          </Sel>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
        <Kpi icon="contratos" tone="var(--ok)" label="Contratos vigentes" value={ativos.length}
          sub={<span>{emDia} em dia · {aRenovar30.length} a renovar · de {dados.length}</span>} />
        <Kpi icon="clock" tone="var(--booker-orange)" label="A renovar · 30 dias" value={aRenovar30.length}
          sub={<span>renovação automática no aniversário</span>} />
        <Kpi icon="bell" tone="var(--text-muted)" label="Encerrados" value={encerradosN}
          sub={<span>fora da carteira ativa</span>} />
        <Kpi icon="dollar" tone="var(--booker-orange)" label="Correção pelo índice" value={fmtBRL(totalDiverg)} small
          sub={<span>{divergencias.length} abaixo do índice · consultivo</span>} />
      </div>

      {/* Row 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: 16, marginBottom: 16 }}>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-strong)' }}>Renovações · próximos 6 meses</h3>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>Valor recorrente que renova por mês de aniversário</p>
            </div>
            <Button size="sm" variant="ghost" icon="external" onClick={() => onNavigate('contratos')}>Ver agenda</Button>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, height: 168 }}>
            {meses.map((m, i) => {
              const max = Math.max(...meses.map(x => x.valor), 1);
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, height: '100%', justifyContent: 'flex-end' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: m.count ? 'var(--text-strong)' : 'var(--text-faint)' }}>{m.count || ''}</span>
                  <div style={{ width: '100%', maxWidth: 46, height: Math.max(6, (m.valor / max) * 120), borderRadius: 8,
                    background: i === 0 ? 'var(--booker-orange)' : 'linear-gradient(180deg, var(--booker-yellow), var(--booker-yellow-deep))',
                    transformOrigin: 'bottom', animation: `growBar .5s ${i * 0.06}s ease both`, position: 'relative' }} title={fmtBRL(m.valor)} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'capitalize' }}>{m.label}</span>
                </div>
              );
            })}
          </div>
        </Card>

        <Card>
          <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: 'var(--text-strong)' }}>Carteira por status</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 14 }}>
            <Donut segments={statusCounts.map(s => ({ value: s.value, color: stColors[s.label] }))} size={128} stroke={15}
              center={<><span style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-strong)' }}>{dados.length}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>contratos</span></>} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11, flex: 1 }}>
              {statusCounts.map(s => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: stColors[s.label] }} />
                  <span style={{ fontSize: 13, color: 'var(--text)', flex: 1 }}>{s.label}</span>
                  <b style={{ fontSize: 14, color: 'var(--text-strong)' }} className="tnum">{s.value}</b>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
            <span>Vigentes <b style={{ color: 'var(--text-strong)' }}>{ativos.length}</b> · em dia + a renovar</span>
            <span>Total <b style={{ color: 'var(--text-strong)' }}>{dados.length}</b></span>
          </div>
        </Card>
      </div>

      {/* Row 3 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.55fr 1fr', gap: 16 }}>
        <Card pad={0}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 22px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-strong)' }}>Log de alertas e renovações</h3>
              <span style={{ background: 'var(--danger-bg)', color: 'var(--danger)', fontWeight: 800, fontSize: 12, padding: '2px 8px', borderRadius: 99 }}>{alertas.length}</span>
            </div>
            <Button size="sm" variant="ghost" onClick={() => onNavigate('alertas')} iconRight="chevronR">Central</Button>
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {alertas.map((a, i) => (
              <div key={i} onClick={() => onNavigate('detalhe', a.k.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '13px 22px', borderTop: '1px solid var(--border)', cursor: 'pointer', transition: 'background .15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ width: 36, height: 36, borderRadius: 10, background: a.color + '1e', color: a.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name={a.icon} size={18} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.k.cliente.fantasia} · {a.k.codigo}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{a.msg}</div>
                </div>
                <StatusPill status={a.tipo === 'Valor' ? 'Pendente' : 'A renovar'} dot={false} />
              </div>
            ))}
            {!alertas.length && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhum alerta no escopo atual 🎉</div>}
          </div>
        </Card>

        <Card>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--text-strong)' }}>Distribuição por índice</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {idxDist.map(x => {
              const pct = (x.count / Math.max(ativos.length, 1)) * 100;
              return (
                <div key={x.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                    <span style={{ fontWeight: 700, color: x.color }}>{x.label}</span>
                    <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{x.count} contratos · acum. {DB.INDICES[x.key].acum12}%</span>
                  </div>
                  <div style={{ height: 9, background: 'var(--surface-inset)', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: pct + '%', height: '100%', background: x.color, borderRadius: 99, transition: 'width .6s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 20, padding: 14, background: 'var(--surface-inset)', borderRadius: 'var(--r)', display: 'flex', gap: 12, alignItems: 'center' }}>
            <Icon name="trend" size={20} style={{ color: 'var(--booker-orange)' }} />
            <div style={{ fontSize: 12.5, color: 'var(--text)' }}>Valor recorrente ativo da carteira: <b style={{ color: 'var(--text-strong)' }}>{fmtBRL(valorCarteira)}/mês</b></div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ icon, tone, label, value, sub, small }) {
  return (
    <Card hover pad={20}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <span style={{ width: 42, height: 42, borderRadius: 12, background: tone + '1c', color: tone, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name={icon} size={21} />
        </span>
      </div>
      <div style={{ fontSize: small ? 24 : 32, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-.02em', lineHeight: 1.05 }} className="tnum">{value}</div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', marginTop: 6 }}>{label}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>
    </Card>
  );
}

Object.assign(window, { Dashboard });
